import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchSuicideDetail } from "../../../store/slices/suicideDetailsSlice";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

export default function SuicideFormPrint() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const suicideState = useSelector((state) => state.suicideDetails || {});
    const { currentSuicideDetail, isLoading, error } = suicideState;

    useEffect(() => {
        if (id) dispatch(fetchSuicideDetail(id));
    }, [dispatch, id]);

    useEffect(() => {
        if (currentSuicideDetail) {
            const timer = setTimeout(() => window.print(), 800);
            window.onafterprint = () => navigate(-1);
            return () => {
                clearTimeout(timer);
                window.onafterprint = null;
            };
        }
    }, [currentSuicideDetail, navigate]);

    if (isLoading) return <div className="p-8 text-center">Loading Suicide Risk Assessment...</div>;
    if (error) return <div className="p-8 text-danger text-center">{error}</div>;
    if (!currentSuicideDetail) return <div className="p-8 text-center">No record found.</div>;

    const d = currentSuicideDetail;

    const fmtDate = (dateStr) =>
        dateStr ? new Date(dateStr).toLocaleDateString("en-GB", {
            day: "2-digit", month: "short", year: "numeric",
        }) : "";

    const questions = [
        "In the past 4 weeks did you feel so sad that nothing could cheer you up?",
        "In the past 4 weeks, how often did you feel no hope for the future?",
        "In the past 4 weeks, how often did you feel intense shame or guilt?",
        "In the past 4 weeks, how often did you feel worthless?"
    ];

    return (
        <>
            <style jsx global>{`
                * { box-sizing: border-box !important; font-family: Arial, sans-serif !important; }
                body { background: #fff !important; color: #000 !important; font-size: 13px !important; line-height: 1.4 !important; margin: 0 !important; padding: 0 !important; }
                .print-container { padding: 30px !important; position: relative !important; }
                ${printFooterStyles}
                .logo { position: absolute !important; top: 15px !important; left: 20px !important; }
                .logo img { height: 65px !important; width: auto !important; }
                
                .header { text-align: center !important; margin-bottom: 25px !important; border-bottom: 2.5px solid #000 !important; padding-bottom: 10px !important; }
                .header h3 { font-size: 1.6rem !important; font-weight: bold !important; margin-bottom: 5px !important; color: #000 !important; }
                .header h6 { font-size: 1.1rem !important; text-transform: uppercase !important; font-weight: bold !important; }

                .meta-row { display: flex !important; justify-content: space-between !important; margin-bottom: 20px !important; padding: 10px !important; border: 1px solid #000 !important; background: #f9f9f9 !important; }
                .meta-item { font-weight: bold !important; }
                .meta-item span { font-weight: normal !important; text-decoration: underline !important; margin-left: 5px !important; }

                .risk-highlight { border: 3px solid #000 !important; padding: 15px !important; text-align: center !important; margin: 20px 0 !important; background: #f2f2f2 !important; }
                .risk-value { font-size: 1.5rem !important; font-weight: 900 !important; text-transform: uppercase !important; text-decoration: underline !important; }

                .q-block { margin-bottom: 15px !important; border: 1px solid #ccc !important; page-break-inside: avoid !important; }
                .q-header { background: #eee !important; padding: 8px 12px !important; font-weight: bold !important; border-bottom: 1px solid #ccc !important; display: flex !important; justify-content: space-between !important; }
                .q-body { padding: 10px 15px !important; }
                
                .checkbox-grid { display: grid !important; grid-template-columns: repeat(3, 1fr) !important; gap: 10px !important; margin-top: 10px !important; }
                .check-item { display: flex !important; align-items: center !important; }
                .box { width: 16px !important; height: 16px !important; border: 1px solid #000 !important; margin-right: 8px !important; text-align: center !important; line-height: 14px !important; font-weight: bold !important; font-size: 12px !important; }
                .ticked { background: #000 !important; color: #fff !important; }

                .details-table { width: 100% !important; border-collapse: collapse !important; margin-top: 10px !important; }
                .details-table td { border: 1px solid #000 !important; padding: 8px !important; vertical-align: top !important; }
                .label-td { background: #f2f2f2 !important; font-weight: bold !important; width: 30% !important; }

                .footer { margin-top: 40px !important; border-top: 1.5px solid #000 !important; padding-top: 10px !important; text-align: center !important; font-size: 11px !important; }
                .sig-grid { display: grid !important; grid-template-columns: 1fr 1fr !important; gap: 60px !important; margin-top: 40px !important; }
                .sig-box { border-top: 1.5px solid #000 !important; text-align: center !important; padding-top: 5px !important; font-weight: bold !important; }
            `}</style>

            <PrintWrapper>
                <div className="print-container">
                <div className="logo"><img src={logo} alt="TRH Logo" /></div>
                <div className="header">
                    <h3>THE RECOVERY HOUSE</h3>
                    <h6>SUICIDE RISK ASSESSMENT SCREEN</h6>
                </div>

                <div className="meta-row">
                    <div className="meta-item">Client: <span>{d.client_name || d.clientName}</span></div>
                    <div className="meta-item">Completed By: <span>{d.completed_by || d.completedBy}</span></div>
                    <div className="meta-item">Date: <span>{fmtDate(d.date)}</span></div>
                </div>

                {/* Risk Level Highlight */}
                <div className="risk-highlight">
                    <span>WORKER RATED RISK LEVEL:</span>
                    <div className="risk-value">{d.worker_risk_level || d.workerRiskLevel || "PENDING"}</div>
                </div>

                {/* Questions 1-4 */}
                {questions.map((qText, i) => (
                    <div className="q-block" key={i}>
                        <div className="q-header">
                            <span>{i + 1}. {qText}</span>
                            <span style={{ textDecoration: 'underline' }}>{d[`q${i + 1}`]}</span>
                        </div>
                    </div>
                ))}

                {/* Question 5: Previous Attempts */}
                <div className="q-block">
                    <div className="q-header">5. Previous Suicide Attempt(s)? <span>{d.q5?.toUpperCase()}</span></div>
                    {d.q5 === "yes" && (
                        <div className="q-body">
                            <table className="details-table">
                                <tbody>
                                    <tr>
                                        <td className="label-td">Number of Attempts</td>
                                        <td>{d.q5_attempts}</td>
                                    </tr>
                                    <tr>
                                        <td className="label-td">Last Attempt Occurred</td>
                                        <td>{d.q5_last_attempt || d.q5_lastAttempt}</td>
                                    </tr>
                                    <tr>
                                        <td className="label-td">Changes Since Then</td>
                                        <td>{d.q5_changes}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Question 6: Upsetting Events */}
                <div className="q-block">
                    <div className="q-header">6. Recent Upsetting Events? <span>{d.q6?.toUpperCase()}</span></div>
                    {d.q6 === "yes" && (
                        <div className="q-body">
                            <div className="checkbox-grid">
                                {(d.q6_events || []).map(ev => (
                                    <div className="check-item" key={ev}><div className="box ticked">✓</div>{ev}</div>
                                ))}
                                {d.q6_other && <div className="check-item"><div className="box ticked">✓</div>Other: {d.q6_other}</div>}
                            </div>
                        </div>
                    )}
                </div>

                {/* Question 7 & 8 Combined Table for Plan/Intent */}
                <div className="q-block">
                    <div className="q-header">Current Thoughts & Plan Assessment</div>
                    <div className="q-body">
                        <table className="details-table">
                            <tbody>
                                <tr>
                                    <td className="label-td">Current Suicidal Thoughts</td>
                                    <td>{d.q7?.toUpperCase()} {d.q7 === "yes" && `(Freq: ${d.q7_frequency}, Dur: ${d.q7_duration})`}</td>
                                </tr>
                                {d.q7 === "yes" && (
                                    <tr>
                                        <td className="label-td">Intensity (Severe/Weekly)</td>
                                        <td>Max: {d.q7_intensity_severe || d.q7_intensitySevere} / Weekly: {d.q7_intensity_week || d.q7_intensityWeek}</td>
                                    </tr>
                                )}
                                <tr>
                                    <td className="label-td">Suicide Plan</td>
                                    <td>{d.q8?.toUpperCase()} {d.q8 === "yes" && `(Method: ${d.q8_method})`}</td>
                                </tr>
                                {d.q8 === "yes" && (
                                    <>
                                        <tr>
                                            <td className="label-td">Location & Likelihood</td>
                                            <td>Loc: {d.q8_location} / Likelihood: {d.q8_likelihood}</td>
                                        </tr>
                                        <tr>
                                            <td className="label-td">Access to Means / Preps</td>
                                            <td>Means: {d.q8_access?.toUpperCase()} / Prep: {d.q8_preparations?.toUpperCase()}</td>
                                        </tr>
                                    </>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Presentation Section */}
                <div className="q-block">
                    <div className="q-header">Clinical Presentation</div>
                    <div className="q-body">
                        <div className="checkbox-grid">
                            {(d.presentation || []).map(p => (
                                <div className="check-item" key={p}><div className="box ticked">✓</div>{p}</div>
                            ))}
                            {d.presentation_other && <div className="check-item"><div className="box ticked">✓</div>Other: {d.presentation_other}</div>}
                        </div>
                    </div>
                </div>

                {/* Barriers & Support */}
                <div className="q-block">
                    <div className="q-header">Barriers to Action & Support Network</div>
                    <div className="q-body">
                        <p><strong>What stopped you acting:</strong> {d.q9_stopped}</p>
                        <p style={{ marginTop: '10px' }}><strong>Support People:</strong> {d.q10_people} ({d.q10_contact})</p>
                        <p style={{ marginTop: '10px' }}><strong>Past Coping:</strong> {d.q11_past_help || d.q11_pastHelp}</p>
                    </div>
                </div>

                <div className="sig-grid">
                    <div className="sig-box">Assessing Worker Signature</div>
                    <div className="sig-box">Clinical Supervisor / Doctor Signature</div>
                </div>

                <div className="footer">
                    <p>156 Bangalore Town, Tipu Sultan Road Karachi-Pakistan | Ph: 021-34546364-65-66</p>
                    <p><strong>A Project of “CARVAN OF LIFE PAKISTAN TRUST”</strong></p>
                </div>
            </div>
            </PrintWrapper>
        </>
    );
}